/**
 * Entity ID Utilities
 *
 * TypeScript functions to convert between long and short ID formats:
 * - Long format: "00010003-01234567-89ab-cdef-1234-567890abcdef"
 * - Short format: Base64URL encoded representation with trailing zeros removed
 */

/**
 * Interface for parsed Entity ID components
 */
interface EntityIdComponents {
  apiId: number
  keyId: string
}

/**
 * Error thrown when an invalid entity ID is encountered
 */
class InvalidEntityIdError extends Error {
  constructor(entityId: string) {
    super(`Invalid entity ID format: ${entityId}`)
    this.name = "InvalidEntityIdError"
  }
}

/**
 * Converts a UUID string to bytes in little-endian order
 * @param uuid - UUID in format "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
 * @returns 16 bytes in little-endian order
 */
function uuidToBEBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "")

  const bytes = new Uint8Array(16)
  for (let i = 0; i < 16; i++) {
    bytes[i] = Number.parseInt(hex.substring((i * 2), (i * 2) + 2), 16)
  }

  return bytes
}

/**
 * Converts little-endian bytes to a UUID string
 * @param bytes - 16 bytes in little-endian order
 * @returns UUID in format "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
 */
function beBytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new Error(`Invalid byte length for UUID: ${bytes.length}`)
  }

  // Convert bytes to hex parts
  const parts = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("")

  return `${parts.substring(0, 8)}-${parts.substring(8, 12)}-${parts.substring(12, 16)}-${parts.substring(16, 20)}-${parts.substring(20)}`
}

/**
 * Converts a UUID string to a number
 * @param uuid - UUID in format "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
 * @returns Number representation (may lose precision for large UUIDs)
 */
function uuidToNumber(uuid: string): number {
  const hex = uuid.replace(/-/g, "")
  return Number.parseInt(hex, 16)
}

/**
 * Converts a number to a UUID string
 * @param num - The number to convert
 * @returns UUID in format "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
 * @throws Error if the number is negative
 */
function numberToUuid(num: number): string {
  if (num < 0) {
    throw new Error("Cannot convert negative number to UUID")
  }

  // Convert number to hex and pad to at least 32 characters (128 bits)
  const hex = num.toString(16).padStart(32, "0")

  // Format as UUID
  return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20)}`
}

/**
 * Converts a number to little-endian bytes
 * @param num - The number to convert
 * @param byteLength - The number of bytes to use
 * @returns bytes in little-endian order
 */
function toLeBytes(num: number, byteLength: number): Uint8Array {
  const bytes = new Uint8Array(byteLength)
  for (let i = 0; i < byteLength; i++)
    bytes[i] = (num >> (8 * i)) & 0xff
  return bytes
}

/**
 * Converts little-endian bytes to a number
 * @param bytes - Bytes in little-endian order
 * @returns The resulting number
 */
function fromLeBytes(bytes: Uint8Array): number {
  let result = 0
  for (let i = 0; i < bytes.length; i++)
    result += bytes[i] * 256 ** i
  return result
}

/**
 * Base64URL encoding (with no padding)
 * @param bytes - The bytes to encode
 * @returns Base64URL encoded string
 */
function base64UrlEncode(bytes: Uint8Array): string {
  // Convert bytes to base64 using btoa
  const base64 = uint8ArrayToBase64(bytes)

  // Convert to base64url
  return base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "") // Remove padding
}

/**
 * Base64URL decoding
 * @param str - The Base64URL encoded string
 * @returns The decoded bytes
 */
function base64UrlDecode(str: string): Uint8Array {
  // Convert base64url to regular base64
  let base64 = str
    .replace(/-/g, "+")
    .replace(/_/g, "/")

  // Add padding if needed
  while (base64.length % 4)
    base64 += "="

  return base64ToUint8Array(base64)
}

/**
 * Parse a long ID string into components
 * @param longId - Long ID string (e.g. "00010003-01234567-89ab-cdef-1234-567890abcdef")
 * @returns Object with apiId and keyId properties
 * @throws InvalidEntityIdError if the longId format is invalid
 */
function parseLongId(longId: string): EntityIdComponents {
  const parts = longId.split("-")
  if (parts.length !== 6)
    throw new InvalidEntityIdError(longId)

  // First part should contain apiId in the format "00010003"
  const apiIdHex = parts[0]
  if (apiIdHex.length !== 8)
    throw new InvalidEntityIdError(longId)

  // The apiId is the decimal value of the hex string
  const apiId = Number.parseInt(apiIdHex, 16)

  // The rest of the parts form the UUID
  const keyId = parts.slice(1).join("-")

  return { apiId, keyId }
}

/**
 * Format components into a long ID string
 * @param apiId - API ID number
 * @param keyId - UUID string
 * @returns Long ID string
 */
function formatLongId(apiId: number, keyId: string): string {
  // Format apiId as 8-digit hex
  const apiIdHex = apiId.toString(16).padStart(8, "0")

  return `${apiIdHex}-${keyId}`
}

/**
 * Convert a long ID to a short ID
 * @param longId - Long ID string (e.g. "00010003-01234567-89ab-cdef-1234-567890abcdef")
 * @returns Short ID string (Base64URL encoded)
 * @throws InvalidEntityIdError if the longId format is invalid
 */
function asShortId(longId: string): string {
  // Parse the long ID into components
  const { apiId, keyId } = parseLongId(longId)

  // Convert keyId to LE bytes
  const keyBytes = uuidToBEBytes(keyId)

  // Convert apiId to LE bytes
  const apiBytes = toLeBytes(apiId, 4)

  // Combine the bytes: key followed by api
  const combinedBytes = new Uint8Array(20)
  combinedBytes.set(keyBytes, 0)
  combinedBytes.set(apiBytes, 16)

  // Find the position of the last non-zero byte
  let lastNonZero = combinedBytes.length - 1
  while (lastNonZero >= 0 && combinedBytes[lastNonZero] === 0)
    lastNonZero--

  // If all bytes are zero, use a single zero byte
  const bytesToEncode = lastNonZero < 0
    ? combinedBytes.slice(0, 1)
    : combinedBytes.slice(0, lastNonZero + 1)

  // Encode using Base64URL without padding
  return base64UrlEncode(bytesToEncode)
}

/**
 * Convert a short ID to a long ID
 * @param shortId - Short ID string (Base64URL encoded)
 * @returns Long ID string
 * @throws Error if the shortId cannot be decoded properly
 */
function fromShortId(shortId: string): string {
  try {
    // Decode the Base64URL string
    const decoded = base64UrlDecode(shortId)

    // Ensure we have 20 bytes (pad with zeros if needed)
    const fullBytes = new Uint8Array(20)
    fullBytes.set(decoded, 0)

    // Extract the key and api parts
    const keyBytes = fullBytes.slice(0, 16)
    const apiBytes = fullBytes.slice(16, 20)

    // Convert bytes to components
    const keyId = beBytesToUuid(keyBytes)
    const apiId = fromLeBytes(apiBytes)

    // Format as long ID
    return formatLongId(apiId, keyId)
  }
  catch {
    throw new InvalidEntityIdError(shortId)
  }
}

// Export as module for Node.js or as global for browser
export {
  asShortId,
  fromShortId,
  InvalidEntityIdError,
  numberToUuid,
  uuidToNumber,
}
